import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  EnvironmentProviders,
  importProvidersFrom,
  Provider,
} from '@angular/core';
import { OktaAuthModule, OktaConfig } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { AppConfig } from './config/app-config.token';
import { AuthGuard } from './guards/auth.guard';
import { PreAuthGuard } from './guards/pre-auth.guard';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { AUTHENTICATION_CONFIGURATION_NAMES } from './services/auth/authenticationConfigNames';

/**
 * Authentication providers, built from the RUNTIME configuration.
 *
 * This was previously an `@NgModule` (auth.module.ts) that read
 * `environment.auth` at module-evaluation time, which made Okta's provider
 * registration a build-time decision. It now receives the resolved runtime
 * config (see `main.ts` / `loadAppConfig`), so which auth providers are
 * registered is decided per tenant at runtime.
 */
export function provideAuth(
  config: AppConfig,
): (Provider | EnvironmentProviders)[] {
  const providers: (Provider | EnvironmentProviders)[] = [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    PreAuthGuard,
    AuthGuard,
  ];

  const authSetup: Record<string, any> = config.auth.reduce(
    (a, v) => ({ ...a, [v.authConfigName]: v }),
    {} as Record<string, any>,
  );

  const okta = authSetup[AUTHENTICATION_CONFIGURATION_NAMES.OKTA];
  if (okta && okta.enabled) {
    const useSqeptechIssuer = !!(
      window.location.search &&
      window.location.search.includes('login=esqep') &&
      okta.sqeptechOpenIdIssuer
    );

    const oktaAuth = new OktaAuth({
      issuer: useSqeptechIssuer ? okta.sqeptechOpenIdIssuer : okta.openIdIssuer,
      clientId: useSqeptechIssuer ? okta.sqeptechClientId : okta.clientId,
      redirectUri: window.location.origin + '/login/callback',
    });

    const moduleConfig: OktaConfig = { oktaAuth };
    providers.push(importProvidersFrom(OktaAuthModule.forRoot(moduleConfig)));
  }

  return providers;
}
